import { ChevronDown, ChevronRight, Search } from "lucide-react";
import { type ReactNode, useEffect, useMemo, useRef, useState } from "react";
import { cn } from "../../lib/utils";

export type SectionMode = "recientes" | "top" | "alpha";

export interface SectionItem {
  id: string;
  label: string;
  count?: number;
  updatedAt?: string;
  href?: string;
  active?: boolean;
}

interface Props {
  title: string;
  items: SectionItem[];
  /** Subset of ids that should rank first in 'recientes' mode. */
  recentIds?: string[];
  defaultOpen?: boolean;
  /** Hide search + mode selector when items.length is below this. */
  searchThreshold?: number;
  /** Max items to show before "Ver todos". */
  visibleCap?: number;
  /** "Ver todos →" target. */
  seeAllHref?: string;
  renderItem: (item: SectionItem) => ReactNode;
  emptyLabel?: string;
  storageKey?: string;
}

/**
 * Linear-style collapsible sidebar section with per-section search and a
 * mode selector (Recientes / Top / A-Z). Below `searchThreshold` items the
 * search controls are hidden and the section behaves like a plain list.
 */
export function SectionWithSearch({
  title,
  items,
  recentIds = [],
  defaultOpen = true,
  searchThreshold = 8,
  visibleCap = 8,
  seeAllHref,
  renderItem,
  emptyLabel,
  storageKey,
}: Props) {
  const [open, setOpen] = useState(() => {
    if (!storageKey) return defaultOpen;
    try {
      const stored = localStorage.getItem(`section:open:${storageKey}`);
      if (stored == null) return defaultOpen;
      return stored === "1";
    } catch {
      return defaultOpen;
    }
  });
  const [query, setQuery] = useState("");
  const [mode, setMode] = useState<SectionMode>("recientes");
  const inputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (!storageKey) return;
    try {
      localStorage.setItem(`section:open:${storageKey}`, open ? "1" : "0");
    } catch {
      /* ignore */
    }
  }, [open, storageKey]);

  const showControls = items.length >= searchThreshold;
  const ranked = useMemo(() => rankItems(items, mode, recentIds), [items, mode, recentIds]);
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return ranked;
    return ranked.filter((it) => it.label.toLowerCase().includes(q));
  }, [ranked, query]);
  const visible = filtered.slice(0, visibleCap);
  const overflow = filtered.length > visibleCap;

  return (
    <section data-section={title} className="px-2">
      <header className="flex items-center justify-between gap-1 px-2 pb-1 pt-3 text-[10px] font-semibold uppercase tracking-wider text-(--text-tertiary)">
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          className="inline-flex items-center gap-1 hover:text-(--text-secondary)"
          aria-expanded={open}
        >
          {open ? <ChevronDown size={10} /> : <ChevronRight size={10} />}
          {title}
        </button>
        {showControls && open && (
          <ModeSelector
            mode={mode}
            onChange={(m) => {
              setMode(m);
              window.setTimeout(() => inputRef.current?.focus(), 30);
            }}
          />
        )}
      </header>

      {open && (
        <>
          {showControls && (
            <div className="relative mb-1 px-1">
              <Search
                size={11}
                className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-(--text-tertiary)"
              />
              <input
                ref={inputRef}
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Buscar…"
                aria-label={`Buscar en ${title}`}
                className="h-7 w-full rounded-md border border-(--border-subtle) bg-(--bg-base) pl-7 pr-2 text-xs placeholder:text-(--text-tertiary) focus:outline-none focus:ring-1 focus:ring-(--accent-500)"
              />
            </div>
          )}

          <div className="flex flex-col gap-0.5" aria-label={title}>
            {visible.length === 0 && (
              <p className="px-2 py-1.5 text-xs text-(--text-tertiary)">
                {query ? "Sin coincidencias" : (emptyLabel ?? "Vacío")}
              </p>
            )}
            {visible.map((item) => (
              <div key={item.id}>{renderItem(item)}</div>
            ))}
          </div>

          {overflow && seeAllHref && (
            <a
              href={seeAllHref}
              className="mt-1 block px-2 py-1 text-[11px] text-(--accent-500) hover:text-(--accent-600)"
            >
              Ver todos ({filtered.length}) →
            </a>
          )}
        </>
      )}
    </section>
  );
}

function ModeSelector({
  mode,
  onChange,
}: {
  mode: SectionMode;
  onChange: (mode: SectionMode) => void;
}) {
  const options: { id: SectionMode; label: string; full: string }[] = [
    { id: "recientes", label: "R", full: "Recientes" },
    { id: "top", label: "T", full: "Top" },
    { id: "alpha", label: "A", full: "A-Z" },
  ];
  return (
    <div className="flex items-center gap-px rounded-md border border-(--border-subtle) bg-(--bg-base) p-px">
      {options.map((opt) => (
        <button
          key={opt.id}
          type="button"
          onClick={() => onChange(opt.id)}
          title={opt.full}
          aria-label={opt.full}
          aria-pressed={mode === opt.id}
          className={cn(
            "rounded-sm px-1 text-[9px] font-medium uppercase tracking-wide transition-colors",
            mode === opt.id
              ? "bg-(--accent-500) text-white"
              : "text-(--text-tertiary) hover:text-(--text-secondary)",
          )}
        >
          {opt.label}
        </button>
      ))}
    </div>
  );
}

export function rankItems(
  items: SectionItem[],
  mode: SectionMode,
  recentIds: string[],
): SectionItem[] {
  if (mode === "alpha") {
    return [...items].sort((a, b) => a.label.localeCompare(b.label));
  }
  if (mode === "top") {
    return [...items].sort((a, b) => {
      const ca = a.count ?? 0;
      const cb = b.count ?? 0;
      if (cb !== ca) return cb - ca;
      return a.label.localeCompare(b.label);
    });
  }
  // recientes: recentIds first (in their order), rest by updatedAt desc, then by label.
  const recentSet = new Map(recentIds.map((id, idx) => [id, idx]));
  return [...items].sort((a, b) => {
    const ra = recentSet.has(a.id)
      ? (recentSet.get(a.id) ?? Number.POSITIVE_INFINITY)
      : Number.POSITIVE_INFINITY;
    const rb = recentSet.has(b.id)
      ? (recentSet.get(b.id) ?? Number.POSITIVE_INFINITY)
      : Number.POSITIVE_INFINITY;
    if (ra !== rb) return ra - rb;
    if (a.updatedAt && b.updatedAt) {
      if (a.updatedAt > b.updatedAt) return -1;
      if (a.updatedAt < b.updatedAt) return 1;
    } else if (a.updatedAt) return -1;
    else if (b.updatedAt) return 1;
    return a.label.localeCompare(b.label);
  });
}
